// Generated by Magic Patterns for every prototype. Not part of the OS tree.
import React, { type ComponentType, useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'

declare global {
  interface ImportMeta {
    glob(
      pattern: string | string[],
      options?: { eager?: boolean; query?: string; import?: string }
    ): Record<string, unknown>
    readonly hot?: {
      data: { root?: ReturnType<typeof ReactDOM.createRoot>; report?: string }
    }
  }
}

type Props = Record<string, unknown>
type Module = { default?: ComponentType<Props> }

import.meta.glob('./index.css', { eager: true })
const prototype = import.meta.glob('./index.tsx', { eager: true }) as Record<string, Module>
const frames = import.meta.glob('./*.frame/index.tsx', { eager: true }) as Record<string, Module>
const frameTakes = import.meta.glob('./*.exploration/frames/*.tsx', { eager: true }) as Record<string, Module>
const prototypeTakes = import.meta.glob('./*.exploration/prototypes/*.tsx', { eager: true }) as Record<string, Module>
const sources = import.meta.glob(
  [
    './index.tsx',
    './*.frame/index.tsx',
    './*.exploration/index.md',
    './*.exploration/frames/*.tsx',
    './*.exploration/prototypes/*.tsx',
  ],
  { eager: true, query: '?raw', import: 'default' }
) as Record<string, string>
const embedded = typeof window !== 'undefined' && window.parent !== window
const hot = import.meta.hot

// Again on every hot update, so the canvas hears of files as they land. Only
// when they changed: every frame showing this prototype runs it.
if (embedded) {
  const files = Object.entries(sources).map(([path, code]) => ({ path: path.slice(2), code }))
  const report = JSON.stringify(files)
  if (hot?.data.report !== report) {
    if (hot) hot.data.report = report
    window.parent.postMessage({ type: 'mp:frames-bundle', files }, '*')
  }
}

/**
 * Exported so this module is a Fast Refresh boundary: an edit re-renders it in
 * place. With nothing exported, every edit reloads every frame.
 */
export function Root() {
  useReportBackground()
  const path = window.location.pathname
  const take = takeAfter(path)
  if (take) {
    const modules = take.kind === 'frames' ? frameTakes : prototypeTakes
    const file = `./${take.explorationId}.exploration/${take.kind}/${take.takeId}.tsx`
    const id = `${take.explorationId}/${take.takeId}`
    return take.kind === 'frames' ? (
      <FrameView id={id} Frame={modules[file]?.default} file={file} />
    ) : (
      <Player id={id} Prototype={modules[file]?.default} file={file} />
    )
  }
  const frameId = idAfter(path, '/frames/')
  if (frameId) {
    const [explorationId, takeId] = frameId.split('/')
    const file = takeId
      ? `./${explorationId}.exploration/frames/${takeId}.tsx`
      : `./${frameId}.frame/index.tsx`
    const Frame = (takeId ? frameTakes : frames)[file]?.default
    return <FrameView id={frameId} Frame={Frame} file={file} />
  }
  const prototypeId = idAfter(path, '/prototypes/')
  const [explorationId, takeId] = (prototypeId ?? '').split('/')
  if (takeId) {
    const file = `./${explorationId}.exploration/prototypes/${takeId}.tsx`
    return <Player id={prototypeId ?? ''} Prototype={prototypeTakes[file]?.default} file={file} />
  }
  return <Player id={prototypeId ?? ''} Prototype={prototype['./index.tsx']?.default} file="./index.tsx" />
}

/**
 * A prototype or a prototype take, opened on the frame `?start=` names and
 * played with whatever the canvas's controls are set to on top of its `args`.
 */
function Player({ id, Prototype, file }: { id: string; Prototype?: ComponentType<Props>; file: string }) {
  const start = new URLSearchParams(window.location.search).get('start') ?? undefined
  const live = usePlayedProps(id)
  if (Prototype) return <Prototype {...live} start={start} />
  const first = Object.keys(frames).sort()[0]
  const Frame = first ? frames[first]?.default : undefined
  if (Frame && file === './index.tsx') return <Frame />
  return <Missing message={`${file.slice(2)} has no default export.`} />
}

/** One frame on its own, with whatever the canvas's controls are showing on top. */
function FrameView({ id, Frame, file }: { id: string; Frame?: ComponentType<Props>; file: string }) {
  const live = useLiveProps(id)
  return Frame ? <Frame {...live} /> : <Missing message={`${file.slice(2)} has no default export.`} />
}

/** The props the canvas is showing while one of its controls changes. */
function useLiveProps(id: string): Props {
  const [live, setLive] = useState<Props>({})
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || data.type !== 'mp:frame-props' || data.frameId !== id) return
      setLive(data.props && typeof data.props === 'object' ? data.props : {})
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [id])
  return live
}

/**
 * The values a prototype's controls are set to. Asked for on every start, since
 * a restart is a fresh page and the canvas holds them, not the source.
 */
function usePlayedProps(id: string): Props {
  const [live, setLive] = useState<Props>({})
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || data.type !== 'mp:prototype-props' || data.prototypeId !== id) return
      setLive(data.props && typeof data.props === 'object' ? data.props : {})
    }
    window.addEventListener('message', onMessage)
    if (embedded && id) {
      window.parent.postMessage({ type: 'mp:prototype-props-request', prototypeId: id }, '*')
    }
    return () => window.removeEventListener('message', onMessage)
  }, [id])
  return live
}

/** Tells the canvas what colour sits behind the screen, so it can letterbox in it. */
function useReportBackground() {
  useEffect(() => {
    if (!embedded) return
    let last = ''
    const report = () => {
      let node: Element | null = document.elementFromPoint(2, 2)
      let color = ''
      while (node) {
        const value = getComputedStyle(node).backgroundColor
        if (value && value !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(value)) {
          color = value
          break
        }
        node = node.parentElement
      }
      color = color || '#ffffff'
      if (color === last) return
      last = color
      window.parent.postMessage({ type: 'mp:frames-background', color }, '*')
    }
    report()
    const timer = window.setInterval(report, 1000)
    return () => window.clearInterval(timer)
  }, [])
}

function takeAfter(path: string): { explorationId: string; kind: 'frames' | 'prototypes'; takeId: string } | null {
  const at = path.lastIndexOf('/explorations/')
  if (at === -1) return null
  const [explorationId, kind, takeId] = path.slice(at + '/explorations/'.length).split('/').map(decode)
  if (!explorationId || !takeId || (kind !== 'frames' && kind !== 'prototypes')) return null
  return { explorationId, kind, takeId }
}

function idAfter(path: string, prefix: string): string | null {
  const at = path.lastIndexOf(prefix)
  if (at === -1) return null
  const id = path.slice(at + prefix.length).split('/')[0]
  return id ? decode(id) : null
}

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

function Missing({ message }: { message: string }) {
  return (
    <div className="flex min-h-full w-full items-center justify-center p-6 text-center text-sm text-slate-500">
      {message}
    </div>
  )
}

// One root per page. A hot update runs this module again, and Fast Refresh
// re-renders what it redefined inside the root already there.
const rootEl = document.getElementById('root')
if (rootEl && !hot?.data.root) {
  const root = ReactDOM.createRoot(rootEl)
  root.render(<Root />)
  if (hot) hot.data.root = root
}
